import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { SquaresPlusIcon, TrashIcon, PencilIcon, InboxIcon } from '@heroicons/react/24/solid';
import { formatCurrency } from '../../utils/formatting';

// --- Reusable Form Components ---
const FormInput = ({ id, label, type, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type={type} id={id} name={id} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);

const TextArea = ({ id, label, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <textarea id={id} name={id} value={value} onChange={onChange} required={required} rows="4" className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);

const FileInput = ({ id, label, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type="file" id={id} name="image" onChange={onChange} required={required} accept="image/png, image/jpeg, image/webp" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-semibold file:bg-orange-50 dark:file:bg-orange-900/50 file:text-orange-700 dark:file:text-orange-200 hover:file:bg-orange-100 dark:hover:file:bg-orange-900" />
    </div>
);

const CheckboxInput = ({ id, label, checked, onChange }) => (
    <div className="flex items-center">
        <input
            type="checkbox"
            id={id}
            name={id}
            checked={checked}
            onChange={onChange}
            className="h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
        />
        <label htmlFor={id} className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            {label}
        </label>
    </div>
);

// --- Secure S3 Upload Helper ---
async function uploadFileToS3(file, token) {
  const getUrlResponse = await fetch(`${process.env.REACT_APP_API_URL}/onboarding/generate-presigned-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ fileName: file.name, fileType: file.type })
  });
  if (!getUrlResponse.ok) throw new Error('Could not get an upload URL from server.');
  const { presignedUrl, key } = await getUrlResponse.json();
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file
  });
  if (!uploadResponse.ok) throw new Error('Failed to upload product image to S3.');
  return key;
}

// --- Edit Product Modal Component ---
const EditProductModal = ({ product, onClose, onSave, isSubmitting }) => {
    const [formData, setFormData] = useState({
        ...product,
        specifications: JSON.stringify(product.specifications || {}, null, 2)
    });
    
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev, 
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Parse specifications back to object
        const dataToSave = {
            ...formData,
            specifications: JSON.parse(formData.specifications),
            price: parseFloat(formData.price),
            max_quantity: parseInt(formData.max_quantity)
        };
        onSave(dataToSave);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Organization Product</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput 
                            id="name" 
                            name="name" 
                            label="Product Name" 
                            type="text" 
                            value={formData.name} 
                            onChange={handleChange} 
                        />
                        
                        <FormInput 
                            id="price" 
                            name="price" 
                            label="Price (₦)" 
                            type="number" 
                            step="0.01"
                            value={formData.price} 
                            onChange={handleChange} 
                        />
                        
                        <FormInput 
                            id="max_quantity" 
                            name="max_quantity" 
                            label="Max Quantity" 
                            type="number" 
                            value={formData.max_quantity} 
                            onChange={handleChange} 
                        />
                        
                        <FormInput 
                            id="image_url" 
                            name="image_url" 
                            label="Image URL" 
                            type="url" 
                            value={formData.image_url} 
                            onChange={handleChange} 
                            required={false}
                        />
                    </div>
                    
                    <TextArea 
                        id="description" 
                        name="description" 
                        label="Description" 
                        value={formData.description} 
                        onChange={handleChange} 
                    />
                    
                    <TextArea 
                        id="specifications" 
                        name="specifications" 
                        label="Specifications (JSON)" 
                        value={formData.specifications} 
                        onChange={handleChange} 
                    />
                    
                    <CheckboxInput 
                        id="is_available" 
                        name="is_available" 
                        label="Available for campaigns" 
                        checked={formData.is_available} 
                        onChange={handleChange} 
                    />
                    
                    <div className="flex justify-end gap-4 pt-4">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            disabled={isSubmitting} 
                            className="py-2 px-4 bg-gray-200 dark:bg-slate-600 font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={isSubmitting} 
                            className="py-2 px-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Main Page Component ---
export default function OrganizationProductsAdmin() {
    const { token } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ 
        name: '', 
        description: '', 
        price: '', 
        max_quantity: '',
        image_url: '',
        is_available: true,
        specifications: '{}'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [editingProduct, setEditingProduct] = useState(null);

    const fetchProducts = useCallback(async () => {
        if (!token) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-products`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Could not fetch organization products.');
            const data = await response.json();
            setProducts(data);
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev, 
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', text: '' });
        
        try {
            // Parse specifications to object
            const productData = {
                ...formData,
                specifications: JSON.parse(formData.specifications),
                price: parseFloat(formData.price),
                max_quantity: parseInt(formData.max_quantity)
            };
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-products`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(productData)
            });
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Failed to add product.');
            
            setMessage({ type: 'success', text: `Successfully added "${data.name}"!` });
            setFormData({ 
                name: '', 
                description: '', 
                price: '', 
                max_quantity: '',
                image_url: '',
                is_available: true,
                specifications: '{}'
            });
            
            await fetchProducts();
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (productId) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-products/${productId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to delete product.');
            }
            
            setMessage({ type: 'success', text: 'Product deleted successfully!' });
            fetchProducts();
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handleEditSave = async (updatedProduct) => {
        setIsSubmitting(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-products/${updatedProduct.id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(updatedProduct)
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to update product.');
            }
            
            setMessage({ type: 'success', text: 'Product updated successfully!' });
            setEditingProduct(null);
            fetchProducts();
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <p className="p-8 text-center text-xl text-gray-600 dark:text-gray-300">Loading products...</p>;

    return (
        <div className="space-y-8">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Manage Organization Products</h1>
            
            {message.text && (
                <p className={`text-center font-semibold p-3 rounded-md ${message.type === 'success' ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200'}`}>
                    {message.text}
                </p>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                    <form onSubmit={handleAddSubmit} className="space-y-6">
                        <div className="flex items-center gap-3">
                            <SquaresPlusIcon className="w-8 h-8 text-orange-500" />
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Add New Product</h2>
                        </div>
                        
                        <FormInput 
                            id="name" 
                            name="name" 
                            label="Product Name" 
                            type="text" 
                            value={formData.name} 
                            onChange={handleChange} 
                        />
                        
                        <FormInput 
                            id="price" 
                            name="price" 
                            label="Price (₦)" 
                            type="number" 
                            step="0.01"
                            value={formData.price} 
                            onChange={handleChange} 
                        />
                        
                        <FormInput 
                            id="max_quantity" 
                            name="max_quantity" 
                            label="Max Quantity" 
                            type="number" 
                            value={formData.max_quantity} 
                            onChange={handleChange} 
                        />
                        
                        <FormInput 
                            id="image_url" 
                            name="image_url" 
                            label="Image URL" 
                            type="url" 
                            value={formData.image_url} 
                            onChange={handleChange} 
                            required={false}
                        />
                        
                        <TextArea 
                            id="description" 
                            name="description" 
                            label="Description" 
                            value={formData.description} 
                            onChange={handleChange} 
                        />
                        
                        <TextArea 
                            id="specifications" 
                            name="specifications" 
                            label="Specifications (JSON)" 
                            value={formData.specifications} 
                            onChange={handleChange} 
                        />
                        
                        <CheckboxInput 
                            id="is_available" 
                            name="is_available" 
                            label="Available for campaigns" 
                            checked={formData.is_available} 
                            onChange={handleChange} 
                        />
                        
                        <button 
                            type="submit" 
                            disabled={isSubmitting} 
                            className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Adding Product...' : 'Add Product to Catalog'}
                        </button>
                    </form>
                </div>

                <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                        Existing Products ({products.length})
                    </h2>
                    
                    <div className="space-y-4">
                        {products.length > 0 ? (
                            products.map(product => (
                                <div key={product.id} className="flex justify-between items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div className="flex items-center gap-4">
                                        {product.image_url && (
                                            <img 
                                                src={product.image_url} 
                                                alt={product.name} 
                                                className="w-16 h-16 object-cover rounded-md" 
                                            />
                                        )}
                                        <div>
                                            <p className="font-bold text-gray-800 dark:text-white">{product.name}</p>
                                            <p className="text-sm text-gray-500">{formatCurrency(product.price)}</p>
                                            <p className="text-sm text-gray-500">Max Qty: {product.max_quantity}</p>
                                            <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                                                product.is_available 
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
                                                    : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                                            }`}>
                                                {product.is_available ? 'Available' : 'Unavailable'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => setEditingProduct(product)} 
                                            className="p-2 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full" 
                                            title="Edit"
                                        >
                                            <PencilIcon className="w-5 h-5"/>
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(product.id)} 
                                            className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full" 
                                            title="Delete"
                                        >
                                            <TrashIcon className="w-5 h-5"/>
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <InboxIcon className="w-16 h-16 mx-auto text-gray-400" />
                                <p className="mt-4 text-lg text-gray-500">No products have been added yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {editingProduct && (
                <EditProductModal 
                    product={editingProduct}
                    onClose={() => setEditingProduct(null)}
                    onSave={handleEditSave}
                    isSubmitting={isSubmitting}
                />
            )}
        </div>
    );
}